import { Checkbox, Input, Radio } from "@sportsmet/ui";
import type { ReactNode } from "react";

import { PoolBannerField } from "../poolBanner/PoolBannerField";
import {
  approveMyselfChecked,
  POOL_NAME_MAX_LENGTH,
  setApproveMyself,
  type typeCreatePoolDraft,
  type typeCreatePoolVisibility,
} from "./createPoolDraft";

export const VISIBILITY_ITEMS = [
  {
    value: "private",
    label: "Private",
    description:
      "Only people with the Invite code or a share link can find it.",
  },
  {
    value: "public",
    label: "Public",
    description: "Anyone can find this pool by its Pool ID.",
  },
] as const;

export type typeCreatePoolBasicsStepProps = {
  draft: typeCreatePoolDraft;
  nameError: string | null;
  onChangeName: (name: string) => void;
  onBlurName: () => void;
  onChangeDraft: (draft: typeCreatePoolDraft) => void;
};

/** Step 2: pool name plus the who-can-join settings, per frame 21.2. */
export const CreatePoolBasicsStep = (
  props: typeCreatePoolBasicsStepProps,
): ReactNode => {
  return (
    <div className="flex flex-col gap-4" data-testid="create-pool-step-basics">
      <Input
        label="Pool name"
        required
        maxLength={POOL_NAME_MAX_LENGTH}
        value={props.draft.name}
        error={props.nameError ?? undefined}
        onChange={(event) => {
          props.onChangeName(event.target.value);
        }}
        onBlur={props.onBlurName}
        data-testid="create-pool-name"
      />
      <PoolBannerField
        currentBannerUrl={null}
        onBannerReady={(mediaId) => {
          props.onChangeDraft({ ...props.draft, bannerMediaId: mediaId });
        }}
        testId="create-pool-banner"
      />
      <Checkbox
        label="Approve new members myself"
        description="When this is on, you approve each person before they can play."
        checked={approveMyselfChecked(props.draft)}
        onCheckedChange={(checked) => {
          props.onChangeDraft(setApproveMyself(props.draft, checked === true));
        }}
      />
      <Checkbox
        label="Let members invite others"
        description="Turn this off and only you can send invites."
        checked={props.draft.memberSharingEnabled}
        onCheckedChange={(checked) => {
          props.onChangeDraft({
            ...props.draft,
            memberSharingEnabled: checked === true,
          });
        }}
      />
      <Checkbox
        label="Chat"
        description="A chat for everyone in the pool. You can turn it off later; nothing gets deleted."
        checked={props.draft.chatEnabled}
        onCheckedChange={(checked) => {
          props.onChangeDraft({
            ...props.draft,
            chatEnabled: checked === true,
          });
        }}
      />
      <Radio
        label="Who can find this pool"
        items={VISIBILITY_ITEMS}
        value={props.draft.visibility}
        onValueChange={(next) => {
          props.onChangeDraft({
            ...props.draft,
            visibility: next as typeCreatePoolVisibility,
          });
        }}
      />
    </div>
  );
};
